import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Care coordination subsection adapter
 * Handles care coordination fields (5 fields)
 */
export class CareCoordinationAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions for all care coordination fields
    const previousAppointments = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_PREVIOUS_APPOINTMENTS
    )?.at(0)

    const futureAppointments = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_FUTURE_APPOINTMENTS
    )?.at(0)

    const careTeamCoordination = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_CARE_TEAM_COORDINATION
    )?.at(0)

    const ordersForTreatment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_ORDERS_FOR_TREATMENT
    )?.at(0)

    const dateOfVerbalCommunication = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION
    )?.at(0)

    return {
      [RecertFormField.CARE_COORDINATION_PREVIOUS_APPOINTMENTS]: previousAppointments,
      [RecertFormField.CARE_COORDINATION_FUTURE_APPOINTMENTS]: futureAppointments,
      [RecertFormField.CARE_COORDINATION_CARE_TEAM_COORDINATION]: careTeamCoordination,
      [RecertFormField.CARE_COORDINATION_ORDERS_FOR_TREATMENT]: ordersForTreatment,
      [RecertFormField.CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION]: dateOfVerbalCommunication
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    const fields = [
      {
        formField: RecertFormField.CARE_COORDINATION_PREVIOUS_APPOINTMENTS,
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_PREVIOUS_APPOINTMENTS
      },
      {
        formField: RecertFormField.CARE_COORDINATION_FUTURE_APPOINTMENTS,
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_FUTURE_APPOINTMENTS
      },
      {
        formField: RecertFormField.CARE_COORDINATION_CARE_TEAM_COORDINATION,
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_CARE_TEAM_COORDINATION
      },
      {
        formField: RecertFormField.CARE_COORDINATION_ORDERS_FOR_TREATMENT,
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_ORDERS_FOR_TREATMENT
      },
      {
        formField: RecertFormField.CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION,
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION
      }
    ]

    for (const { formField, linkId } of fields) {
      if (this.formDataFieldExists(formField)) {
        mutations.push({
          linkId,
          value: this.getFormDataField(formField)
        })
      }
    }

    return mutations
  }
}
